/**
 * Where a floating, draggable panel was last dropped, persisted to localStorage under a caller-supplied
 * key so more than one dock (e.g. Help videos) can each remember their own place.
 *
 * Ported from web-admin's `feedbackDraft.ts` (the feedback dock). Every read and write is wrapped:
 * storage can be full, blocked or disabled, and losing a saved position must never take the app down
 * with it.
 */

export interface DockPosition {
  x: number;
  y: number;
}

export const HELP_VIDEOS_DOCK_POSITION_KEY = "kde.help-videos-position.v1";

/** Never let a dock be dragged so far off-screen that it can't be grabbed back. */
export const MIN_VISIBLE_PX = 48;

/**
 * Keeps a dock reachable.
 *
 * `x` may go negative so the panel's right-hand edge can still be dragged to the right of the
 * viewport, as long as `MIN_VISIBLE_PX` of it stays grabbable. `y` is bounded at the top because the
 * drag handle lives at the top of the panel — push it above zero and there is nothing left to grab.
 *
 * Applied on drop AND on restore AND on resize: a position saved at the edge of a wide monitor would
 * otherwise put the dock outside a laptop's viewport with no way to reach it.
 */
export function clampDockPosition(pos: DockPosition, viewport: { width: number; height: number }, panelWidth = 0): DockPosition {
  return {
    x: Math.min(Math.max(pos.x, MIN_VISIBLE_PX - panelWidth), Math.max(viewport.width - MIN_VISIBLE_PX, 0)),
    y: Math.min(Math.max(pos.y, 0), Math.max(viewport.height - MIN_VISIBLE_PX, 0)),
  };
}

export function loadDockPosition(key: string = HELP_VIDEOS_DOCK_POSITION_KEY): DockPosition | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    const p = parsed as { x?: unknown; y?: unknown };
    if (typeof p.x !== "number" || typeof p.y !== "number" || !Number.isFinite(p.x) || !Number.isFinite(p.y)) return null;
    return { x: p.x, y: p.y };
  } catch {
    return null;
  }
}

export function saveDockPosition(pos: DockPosition, key: string = HELP_VIDEOS_DOCK_POSITION_KEY): void {
  try {
    localStorage.setItem(key, JSON.stringify(pos));
  } catch {
    // Storage full, blocked, or disabled. A lost position is bad; a crash is worse.
  }
}
